import React, { useEffect, useState, useCallback } from "react";
import "./HRResignationReviewPage.css";
import apiClient from "../utils/apiClient";
import SessionHandler from "../components/SessionHandler";

const HRResignationReviewPage = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  /* Modal state */
  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedAction, setSelectedAction] = useState("");

  /* ================= FETCH REQUESTS ================= */
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      // ✅ apiClient already returns JSON
      const data = await apiClient("/resignation/hr/");

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.results)
        ? data.results
        : [];

      const sorted = list.sort((a, b) => {
        if (a.status === "Pending" && b.status !== "Pending") return -1;
        if (a.status !== "Pending" && b.status === "Pending") return 1;
        return new Date(b.submitted_at) - new Date(a.submitted_at);
      });

      setRequests(sorted);
      setFilteredRequests(sorted);
    } catch (err) {
      console.error(err);
      setMessage("❌ Could not load resignation requests.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  /* ================= FILTER ================= */
  useEffect(() => {
    let filtered = [...requests];

    if (statusFilter) {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    if (employeeFilter) {
      filtered = filtered.filter((r) =>
        r.employee_name
          ?.toLowerCase()
          .includes(employeeFilter.toLowerCase())
      );
    }

    if (departmentFilter) {
      filtered = filtered.filter((r) =>
        r.employee_department
          ?.toLowerCase()
          .includes(departmentFilter.toLowerCase())
      );
    }

    setFilteredRequests(filtered);
  }, [statusFilter, employeeFilter, departmentFilter, requests]);

  /* ================= ACTION ================= */
  const handleAction = async (id, action) => {
  setLoading(true);
  setMessage("");

  try {
    await apiClient(`/resignation/hr/${id}/`, {
      method: "PATCH",
      body: JSON.stringify({ status: action }),
    });

    // ✅ Optimistic UI update
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, status: action } : r
      )
    );

    setMessage(`✅ Request ${action} successfully`);

    // ✅ Optional re-fetch (safe)
    fetchRequests();
  } catch (err) {
    console.error(err);
    setMessage(`❌ Failed to ${action} request.`);
  } finally {
    setLoading(false);
  }
};


  /* ================= MODAL CONFIRM ================= */
const confirmAction = () => {
  if (!selectedRequest || !selectedAction) return;

  // ✅ CLOSE MODAL IMMEDIATELY
  setShowModal(false);

  const id = selectedRequest.id;
  const action = selectedAction;

  setSelectedRequest(null);
  setSelectedAction("");

  // ✅ THEN PERFORM ACTION
  handleAction(id, action);
};


  /* ================= COUNTS ================= */
  const totalCount = requests.length;
  const pendingCount = requests.filter((r) => r.status === "Pending").length;
  const approvedCount = requests.filter((r) => r.status === "Approved").length;
  const rejectedCount = requests.filter((r) => r.status === "Rejected").length;

  /* ================= UI ================= */
  return (
    <div className="hr-review-container">
      <h2>Resignation Requests</h2>

      {/* SUMMARY */}
      <div className="request-tiles14">
        <div className="tile14 total">Total: {totalCount}</div>
        <div className="tile14 pending">Pending: {pendingCount}</div>
        <div className="tile14 approved">Approved: {approvedCount}</div>
        <div className="tile14 rejected">Rejected: {rejectedCount}</div>
      </div>

      {/* FILTERS */}
      <div className="hr-filters1">
        <label>Status:</label>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>

        <label>Employee:</label>
        <input
          type="text"
          value={employeeFilter}
          onChange={(e) => setEmployeeFilter(e.target.value)}
          placeholder="Search Employee"
        />

        <label>Department:</label>
        <input
          type="text"
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          placeholder="Search Department"
        />
      </div>

      {loading && <p>Loading...</p>}
      {message && <p className="message">{message}</p>}

      {/* TABLE */}
      <div className="table-wrapper">
        <table className="hr-review-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Resignation Date</th>
              <th>Last Working Date</th>
              <th>Reason</th>
              <th>Submitted Date</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredRequests.length ? (
              filteredRequests.map((req) => (
                <tr key={req.id}>
                  <td>{req.employee_name || "-"}</td>
                  <td>{req.resignation_date}</td>
                  <td>{req.last_working_date || "-"}</td>
                  <td>{req.reason}</td>
                  <td>
                    {req.submitted_at
                      ? new Date(req.submitted_at).toLocaleString()
                      : "-"}
                  </td>
                  <td>
                    {req.status === "Pending" ? (
                      <>
                        <button
                          className="btn-approve"
                          onClick={() => {
                            setSelectedRequest(req);
                            setSelectedAction("Approved");
                            setShowModal(true);
                          }}
                        >
                          Approve
                        </button>
                        <button
                          className="btn-reject"
                          onClick={() => {
                            setSelectedRequest(req);
                            setSelectedAction("Rejected");
                            setShowModal(true);
                          }}
                        >
                          Reject
                        </button>
                      </>
                    ) : (
                      <em>{req.status}</em>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  No resignation requests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* CONFIRM MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>
              {selectedAction === "Approved"
                ? "Approve Resignation"
                : "Reject Resignation"}
            </h3>

            <p>
              Are you sure you want to{" "}
              <strong>{selectedAction.toLowerCase()}</strong> the resignation
              request for{" "}
              <strong>{selectedRequest?.employee_name}</strong>?
            </p>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>

              <button
                className={
                  selectedAction === "Approved"
                    ? "btn-approve"
                    : "btn-reject"
                }
                onClick={confirmAction}
              >
                {selectedAction}
              </button>
            </div>
          </div>
        </div>
      )}

      <SessionHandler />
    </div>
  );
};

export default HRResignationReviewPage;
